if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      // Check if an embroidery option is available and selected.
      const embroideryOption = this.form.querySelector('[data-embroidery-option]');
      const embroideryCheckbox = embroideryOption?.querySelector('[data-embroidery-checkbox]');

      let embroideryVariantId = null;

      if (embroideryCheckbox?.checked) {
        const selectedEmbroideryVariant =
          embroideryOption.querySelector(
            '[data-embroidery-variant]:checked'
          )
          || embroideryOption.querySelector(
            'input[type="hidden"][data-embroidery-variant]'
          );

        embroideryVariantId = selectedEmbroideryVariant?.value || null;
      }

      const quantityInput = this.form.querySelector('[name="quantity"]');
      const quantity = parseInt(quantityInput?.value || '1', 10);

      // Every embroidery order gets its own unique group ID.
      const embroideryGroupId = embroideryCheckbox?.checked
        ? `embroidery_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
        : null;

      // Fixed Shopify variant for embroidery setup costs.
      const embroiderySetupVariantId = 55470674739529;

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      if (embroideryCheckbox?.checked && embroideryVariantId && embroideryGroupId) {
        formData.append('properties[_embroidery_group]', embroideryGroupId);
        formData.append('properties[_embroidery_role]', 'main');
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        /*
        * ----------------------------------------------------------
        * 1. ADD SELECTED EMBROIDERY VARIANT
        * ----------------------------------------------------------
        */

        if (
          embroideryCheckbox?.checked
          && embroideryVariantId
          && embroideryGroupId
        ) {
          const embroideryFormData = new FormData();

          embroideryFormData.append('id', embroideryVariantId);
          embroideryFormData.append('quantity', quantity);

          embroideryFormData.append(
            'properties[_embroidery_group]',
            embroideryGroupId
          );

          embroideryFormData.append(
            'properties[_embroidery_role]',
            'embroidery'
          );

          const embroideryResponse = await fetch(theme.routes.cartAdd, {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: embroideryFormData
          });

          const embroideryData = await embroideryResponse.json();

          if (!embroideryResponse.ok || embroideryData.status) {
            let embroideryError =
              typeof embroideryData.description === 'string'
                ? embroideryData.description
                : embroideryData.message;

            if (!embroideryError) {
              embroideryError = 'Embroidery could not be added to the cart.';
            }

            this.setErrorMsgState(embroideryError);
            throw new Error(embroideryError);
          }
        }

        /*
        * ----------------------------------------------------------
        * 2. ADD EMBROIDERY SETUP COSTS
        * Only once per cart.
        * ----------------------------------------------------------
        */

        const setupCostsAlreadyInCart = oldCartData.items.some(
          (item) => Number(item.variant_id) === embroiderySetupVariantId
        );

        if (
          embroideryCheckbox?.checked
          && embroideryVariantId
          && !setupCostsAlreadyInCart
        ) {
          const setupCostsFormData = new FormData();

          setupCostsFormData.append('id', embroiderySetupVariantId);
          setupCostsFormData.append('quantity', 1);

          /*
          * Setup costs are cart-wide, so they are intentionally
          * NOT linked to one specific embroidery group.
          */
          setupCostsFormData.append(
            'properties[_embroidery_role]',
            'setup'
          );

          const setupCostsResponse = await fetch(theme.routes.cartAdd, {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: setupCostsFormData
          });

          const setupCostsData = await setupCostsResponse.json();

          if (!setupCostsResponse.ok || setupCostsData.status) {
            let setupCostsError =
              typeof setupCostsData.description === 'string'
                ? setupCostsData.description
                : setupCostsData.message;

            if (!setupCostsError) {
              setupCostsError =
                'Embroidery setup costs could not be added to the cart.';
            }

            this.setErrorMsgState(setupCostsError);
            throw new Error(setupCostsError);
          }
        }

        // Add the main product using the normal Enterprise functionality.
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
